import {
  Children,
  isValidElement,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import styled, { css } from "styled-components";

import { ElevatedSurface } from "../Surface";
import { Text, defaultTextVariants } from "../Text";
import { controlFocusRing } from "../foundations/controlStyles";
import { devWarn } from "../../utils/devWarning";
import { isRenderableNode } from "../../utils/reactNode";
import { isNestedInteractiveTarget } from "../../utils/nestedInteractive";
import type {
  CardBodyProps,
  CardFooterProps,
  CardHeaderProps,
  CardLayout,
  CardMediaProps,
  CardProps,
} from "./types";

// clickable 카드의 커서·focus ring(키보드 사용자 한정). 두 variant root가 공유한다.
const clickableCardCss = css`
  cursor: pointer;

  &:focus-visible {
    ${controlFocusRing("3px")}
  }
`;

// elevated variant: ElevatedSurface를 그대로 재사용(elevation tone/shadow/hover) + 미디어 클립.
// `$clickable`은 transient prop이라 ElevatedSurface/DOM으로 forward되지 않는다.
const ElevatedCardRoot = styled(ElevatedSurface)<{ $clickable: boolean }>`
  overflow: hidden;
  ${({ $clickable }) => ($clickable ? clickableCardCss : "")}
`;

// outlined variant: 테두리 기반 평면 룩 + 옅은 hover. 토큰만 사용(임의 색/opacity 없음).
const OutlinedCardRoot = styled.article<{ $clickable: boolean }>`
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surface};
  color: ${({ theme }) => theme.color.text.default};
  transition:
    border-color 140ms ease,
    background-color 140ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.color.border.default};
    background: ${({ theme }) => theme.color.bg.surfaceRaised};
  }

  ${({ $clickable }) => ($clickable ? clickableCardCss : "")}
`;

// 레이아웃 프레임: 미디어 + 콘텐츠를 layout 축으로 배치(미디어는 자체 폭, 콘텐츠가 나머지 채움).
// height:100% — 카드에 명시 높이(예: 그리드 stretch)가 있으면 프레임이 채우고, 없으면 auto로 폴백.
const LayoutFrame = styled.div<{ $layout: CardLayout }>`
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: ${({ $layout }) => ($layout === "horizontal" ? "row" : "column")};
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

// 미디어 frame: 자체 폭(fixedWidth | percentage) + optional 종횡비. 자식이 frame을 가득 채운다.
// block으로 둔다 — flex 컨테이너면 height:100% 자식과 순환 의존이 생겨 aspect-ratio가 무시되고
// frame이 콘텐츠 높이로 줄어든다(미디어 붕괴). block + 확정 width면 aspect-ratio가 높이를 정한다.
const StyledCardMedia = styled.div<{ $width: string; $aspectRatio?: string }>`
  position: relative;
  flex: 0 0 auto;
  overflow: hidden;
  width: ${({ $width }) => $width};
  ${({ $aspectRatio }) => ($aspectRatio ? `aspect-ratio: ${$aspectRatio};` : "")}

  > * {
    /* c6는 전역 box-sizing 리셋이 없다(content-box 기본). 채움 자식의 padding이 height:100%를
       넘쳐 frame 밖으로 잘리지 않도록 border-box로 강제한다. */
    box-sizing: border-box;
    width: 100%;
    height: 100%;
  }

  > img {
    display: block;
    object-fit: cover;
  }
`;

const StyledCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;

  .cardTitleGroup {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  .cardActions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
  }
`;

// Text는 자체 클래스로 color: text.default를 깔기 때문에 &&로 specificity를 올려 덮는다.
const CardTitle = styled(Text)`
  && {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CardSubtitle = styled(Text)`
  && {
    color: ${({ theme }) => theme.color.text.muted};
  }
`;

const StyledCardBody = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
`;

const StyledCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

// fixedWidth XOR percentage. union이 컴파일타임에 강제하지만 런타임 오용도 devWarn으로 잡는다.
function resolveMediaWidth(fixedWidth?: string, percentage?: number): string {
  const hasFixed = fixedWidth !== undefined;
  const hasPercentage = percentage !== undefined;
  if (hasFixed === hasPercentage) {
    devWarn("[c6] <Card.Media> requires exactly one of `fixedWidth` or `percentage`.");
  }
  if (hasFixed) {
    return fixedWidth;
  }
  if (hasPercentage) {
    return `${percentage}%`;
  }
  return "100%";
}

// 미디어 컨테이너. img/gradient/preview 등 임의 노드를 받아 frame을 채운다.
export function CardMedia({
  fixedWidth,
  percentage,
  aspectRatio,
  children,
  className,
}: CardMediaProps) {
  return (
    <StyledCardMedia
      className={className}
      $width={resolveMediaWidth(fixedWidth, percentage)}
      $aspectRatio={aspectRatio}
    >
      {children}
    </StyledCardMedia>
  );
}
CardMedia.displayName = "Card.Media";

// 헤더: title(1줄 ellipsis) + subtitle(muted) 묶음 + 우상단 actions.
export function CardHeader({ title, subtitle, actions, ...rest }: CardHeaderProps) {
  return (
    <StyledCardHeader {...rest}>
      <div className="cardTitleGroup">
        <CardTitle variant={defaultTextVariants["body-bold"]}>{title}</CardTitle>
        {isRenderableNode(subtitle) ? (
          <CardSubtitle variant={defaultTextVariants["caption-normal"]}>{subtitle}</CardSubtitle>
        ) : null}
      </div>
      {isRenderableNode(actions) ? <div className="cardActions">{actions}</div> : null}
    </StyledCardHeader>
  );
}
CardHeader.displayName = "Card.Header";

// 본문: 메타·badge·chip 등을 자유 합성하는 영역.
export function CardBody({ children, ...rest }: CardBodyProps) {
  return <StyledCardBody {...rest}>{children}</StyledCardBody>;
}
CardBody.displayName = "Card.Body";

// 푸터: 액션/캡션 등을 양끝 정렬로 합성하는 영역.
export function CardFooter({ children, ...rest }: CardFooterProps) {
  return <StyledCardFooter {...rest}>{children}</StyledCardFooter>;
}
CardFooter.displayName = "Card.Footer";

// children에서 Card.Media(직접 자식, 첫째)를 분리하고 나머지를 콘텐츠로 둔다.
function partitionCardChildren(children: ReactNode): {
  media: ReactElement | null;
  content: ReactNode[];
} {
  let media: ReactElement | null = null;
  const content: ReactNode[] = [];
  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === CardMedia) {
      if (media === null) {
        media = child;
      } else {
        devWarn("[c6] <Card> accepts a single <Card.Media>; extra media are ignored.");
      }
      continue;
    }
    content.push(child);
  }
  return { media, content };
}

/**
 * 데이터/미디어 카드 표현 primitive (compound).
 *
 * `Card.Media`(직접 자식) + `Card.Header`/`Card.Body`/`Card.Footer`를 조합한다. `layout`이 미디어와
 * 콘텐츠의 축을, `variant`가 표면 스타일을 정한다. `onClick`을 주면 카드 전체가 clickable해지며 중첩
 * 인터랙티브 요소(actions) 클릭은 흡수하지 않는다.
 */
export function Card({
  layout = "vertical",
  variant = "elevated",
  as = "article",
  onClick,
  onKeyDown,
  children,
  ...rest
}: CardProps) {
  const clickable = onClick != null;
  const { media, content } = partitionCardChildren(children);

  // 카드 클릭: 중첩 인터랙티브(actions 등) 위 클릭은 흡수하지 않는다.
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (isNestedInteractiveTarget(event.target, event.currentTarget)) {
      return;
    }
    onClick?.(event);
  };

  // 키보드 활성화: 소비자 onKeyDown 우선, Enter/Space는 실제 click을 디스패치해 onClick 경로로 합류.
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || (event.key !== "Enter" && event.key !== " ")) {
      return;
    }
    if (isNestedInteractiveTarget(event.target, event.currentTarget)) {
      return;
    }
    event.preventDefault();
    event.currentTarget.click();
  };

  const interactiveProps: HTMLAttributes<HTMLElement> = clickable
    ? { role: "button", tabIndex: 0, onClick: handleClick, onKeyDown: handleKeyDown }
    : { onClick, onKeyDown };

  const inner = (
    <LayoutFrame $layout={layout}>
      {media}
      <ContentArea>{content}</ContentArea>
    </LayoutFrame>
  );

  // elevated는 ElevatedSurface를 wrapping하므로 polymorphic element를 `forwardedAs`로 통과시킨다
  // (styled의 `as`는 ElevatedSurface 자체를 교체해 버린다).
  if (variant === "elevated") {
    return (
      <ElevatedCardRoot
        forwardedAs={as}
        elevation={1}
        $clickable={clickable}
        data-layout={layout}
        data-variant="elevated"
        {...interactiveProps}
        {...rest}
      >
        {inner}
      </ElevatedCardRoot>
    );
  }

  return (
    <OutlinedCardRoot
      as={as}
      $clickable={clickable}
      data-layout={layout}
      data-variant="outlined"
      {...interactiveProps}
      {...rest}
    >
      {inner}
    </OutlinedCardRoot>
  );
}
Card.displayName = "Card";

Card.Media = CardMedia;
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
